import React, { forwardRef, memo, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import Input from '@amzn/meridian/input';

import { ICellEditorParams } from 'ag-grid-community';
import { cloneDeep } from 'lodash';
import { AddDealContext } from 'src/contexts/addDealContext';
import { ISpecifyDealAgGridRowErrorState } from 'src/types/IAddDeal';
import { dateYYYYMMReg, getFirstDayOfMonth, getLastDayOfMonth } from 'src/util/dateTimeUtil';
import { dateComparator } from 'src/util/grid/sort';
import { dateYyyyMmMask, emptyDateYyyyMmMaskStr } from 'src/util/inputMask';

import './DateCellEditor.scss';

interface IDateCellEditorParams extends ICellEditorParams {
    isStartDate: boolean;
}

export const DateCellEditor = forwardRef((props: IDateCellEditorParams, ref) => {
    const [value, setValue] = useState(props.value);
    const refInput = useRef<HTMLInputElement>(null);

    const { specifyDealGridRowErrorStates, setSpecifyDealGridRowErrorStates } = useContext(AddDealContext);

    useEffect(() => {
        // focus on the input
        refInput.current!.focus();
    }, []);

    const resetRowErrorState = (clonedErrorStates: ISpecifyDealAgGridRowErrorState[], rowIndex: number) => {
        const newClonedErrorStates = clonedErrorStates.map((row, index) => {
            return index === rowIndex ? { hasError: false, errorMsg: '' } : row;
        });
        setSpecifyDealGridRowErrorStates(newClonedErrorStates);
    };

    const isDisabled = () => {
        return props.isStartDate ? !props.data.contractName : !props.data.contractName || !props.data.startDate;
    };

    /* Component Editor Lifecycle methods */
    useImperativeHandle(ref, () => {
        return {
            // the final value to send to the grid, on completion of editing
            getValue() {
                const isStartDate = props.isStartDate;
                const rowIndex = props.node.rowIndex;

                if (isDisabled()) return '';

                const clonedErrorStates: ISpecifyDealAgGridRowErrorState[] = cloneDeep(specifyDealGridRowErrorStates);

                if (isStartDate) {
                    // empty start date or invalid start date
                    if ((value && value.startsWith(emptyDateYyyyMmMaskStr)) || !dateYYYYMMReg.test(value)) {
                        const newClonedErrorStates = clonedErrorStates.map((row, index) => {
                            return index === rowIndex ? { hasError: true, errorMsg: 'Start date is required' } : row;
                        });
                        setSpecifyDealGridRowErrorStates(newClonedErrorStates);
                        return '';
                    } else {
                        resetRowErrorState(clonedErrorStates, rowIndex!);
                        const newValue = value.substring(0, 7);
                        return getFirstDayOfMonth(newValue);
                    }
                } else {
                    const fullLenValue = getLastDayOfMonth(value);
                    // non-empty end date but invalid or smaller than start date
                    if (
                        value &&
                        !value.startsWith(emptyDateYyyyMmMaskStr) &&
                        (!dateYYYYMMReg.test(value) || dateComparator(fullLenValue, props.data.startDate) <= 0)
                    ) {
                        if (dateComparator(fullLenValue, props.data.startDate) <= 0) {
                            const newClonedErrorStates = clonedErrorStates.map((row, index) => {
                                return index === rowIndex
                                    ? { hasError: true, errorMsg: 'End date must be after start date' }
                                    : row;
                            });
                            setSpecifyDealGridRowErrorStates(newClonedErrorStates);
                        }
                        return '';
                    } else {
                        // empty end date
                        if (value && value.startsWith(emptyDateYyyyMmMaskStr)) {
                            resetRowErrorState(clonedErrorStates, rowIndex!);
                            return '';
                        } else {
                            resetRowErrorState(clonedErrorStates, rowIndex!);
                            const newValue = value.substring(0, 7);
                            return getLastDayOfMonth(newValue);
                        }
                    }
                }
            },

            // Gets called once before editing starts, to give editor a chance to
            // cancel the editing before it even starts.
            isCancelBeforeStart() {
                return false;
            },

            // Gets called once when editing is finished (eg if Enter is pressed).
            // If you return true, then the result of the edit will be ignored.
            isCancelAfterEnd() {}
        };
    });

    return (
        <Input
            ref={refInput}
            className="date-input-editor"
            type="text"
            placeholder="yyyy-mm"
            disabled={isDisabled()}
            mask={dateYyyyMmMask}
            value={value}
            onChange={setValue}
        />
    );
});
